// Usage meter (PR 2): how the plan-minutes meter and the monthly report read. Pure;
// tested in test/usage.test.ts. The server words the line above the dialer and the reset
// time ("Nov 30 at 7 PM", in the reader's zone); this file only lays the numbers out.

import type { UsageLevel, UsageMonth } from './api';
import { count as n } from './plan-text';

/** "142 of 300 minutes used" */
export const usedText = (used: number, bundle: number) => `${n(used)} of ${n(bundle)} minutes used`;

/** "158 minutes left" / "No minutes left" */
export const leftText = (left: number) => (left > 0 ? `${n(left)} ${left === 1 ? 'minute' : 'minutes'} left` : 'No minutes left');

/** How full the bar is, 0–100 (the counter can pass the bundle: never more than 100). */
export function percentUsed(used: number, bundle: number): number {
  if (!(bundle > 0)) return 0;
  return Math.max(0, Math.min(100, Math.round((used / bundle) * 100)));
}

/** The bar's colour: calm until 80 %, then a warning, red when used up. */
export const meterTone = (level: UsageLevel): 'ok' | 'warn' | 'bad' => (level === 'used_up' ? 'bad' : level === 'warn80' ? 'warn' : 'ok');

/** Number and unit stay together when a narrow report cell wraps ("18 / min" never). */
const NB = '\u00a0';

/** A report cell: "3 calls · 12 min", "—" when there were none. */
export function callsText(calls: number, minutes: number): string {
  if (!calls) return '—';
  return `${n(calls)}${NB}${calls === 1 ? 'call' : 'calls'} · ${n(minutes)}${NB}min`;
}

/** The plan-minutes cell: "142 min", "—" (none), "?" (the Call Engine couldn't be read). */
export const planMinutesText = (m: UsageMonth) => (m.planMinutes === null ? '?' : m.planMinutes ? `${n(m.planMinutes)}${NB}min` : '—');

/** The admin list chip for plan minutes (null = nothing to flag). */
export function usageChip(level: UsageLevel | null | undefined, active: boolean): { label: string; tone: 'warn' | 'bad' } | null {
  if (!active || !level || level === 'ok') return null;
  return level === 'used_up' ? { label: 'Minutes used up', tone: 'bad' } : { label: 'Minutes 80%', tone: 'warn' };
}
